// features.js — leximi i regjistrit të veçorive nga ana e aplikacionit (Faza 0)
//
// Kundërpjesa e `feature_catalog`-ut te baza. Modeli i `designLoader`-it: një
// modul i vogël me gjendje, jo context — App.jsx është një komponent i vetëm
// gjigant dhe një context i ri aty do të ishte ceremoni pa dobi.
//
// PËRDORIMI:
//   await loadFeatures(familyId);      // një herë kur zgjidhet familja
//   featureOn('family_book')           // e ZOTËROJMË? — sinkron, kudo në render
//   featureOffered('poster_pro')       // SHITET? — a ka kuptim ta çojmë te vitrina
//   featureGated('poster_pro')         // shitet PO, e zotërojmë JO → vizato bravën
//   featureShown('poster_pro', legacy) // a shfaqet fare — shih më poshtë
//   onFeaturesChange(fn)               // rivizatim kur ndryshojnë
//
// TRI PYETJE, JO NJË (shih FAZA0c/0d/0e te baza):
//
//   MANAGED   a e ka marrë regjistri i ri kontrollin për këtë çelës?
//   OFFERED   a është në vitrinë, pra a blihet dot?
//   ON        a e zotërojmë tani?
//
// Prej tyre dalin dy rregulla, të dy njërreshtësh:
//
//      BRAVË   ⇔  ofrohet && !zotërohet
//      SHFAQET ⇔  managed ? (zotërohet || ofrohet) : porta e vjetër
//
// MANAGED-i është ai që e bën "fshije nga platforma" të mundur. Pa të, dy
// përgjigje `false` do të thoshin njëkohësisht "s'u komercializua kurrë" dhe
// "u fsheh me qëllim" — dhe posteri do të mbetej i dukshëm nga porta e vjetër
// në të dyja rastet. Me të, `off` do të thotë vërtet "si më parë" dhe `hidden`
// do të thotë vërtet "s'ekziston".
//
// Kjo është edhe garancia e pronarit, e zbatuar: OFF = asgjë s'ndryshon.
//
// Vendimi i vërtetë (kush-çfarë-merr) jeton te serveri, te `fn_feature_on` —
// kjo është vetëm pasqyra e tij për vizatim. Një klient i modifikuar mund ta
// gënjejë veten për një buton, por jo serverin për një porosi.
//
// FAIL-CLOSED: pa përgjigje nga serveri, çdo çelës është false. Një veçori që
// mungon për dy sekonda është padukshmëri; një veçori që shfaqet gabimisht
// është premtim i thyer.

import { supabase } from "./supabaseClient";

let _on = {};
let _offered = {};
let _managed = {};
let _familyId = null;
const _listeners = new Set();

/** A e ZOTËROJMË këtë veçori — pra a lejohet përdorimi tani. */
export function featureOn(key) {
  return _on[key] === true;
}

/** A ËSHTË NË VITRINË — pra a ka kuptim ta çojmë përdoruesin te blerja. */
export function featureOffered(key) {
  return _offered[key] === true;
}

/**
 * A duhet vizatuar BRAVA? Vetëm kur veçoria shitet realisht dhe familja s'e ka.
 *
 * Çelësi që s'ekziston fare te katalogu → false: veçoria s'është komercializuar,
 * pra mbetet e lirë. Çelësi që ekziston por është 'off' → gjithashtu false:
 * pronari po e përgatit, klienti s'duhet ta ndiejë. Vetëm 'on' (ose 'beta' me
 * ftesë) e hap bravën — dhe pas saj ka gjithmonë një çmim ku të klikohet.
 */
export function featureGated(key) {
  return _offered[key] === true && _on[key] !== true;
}

/** A ka marrë regjistri i ri kontrollin për këtë çelës (gjendja s'është 'off')? */
export function featureManaged(key) {
  return _managed[key] === true;
}

/**
 * A shfaqet fare kjo veçori — butoni, kartela, rreshti te lista?
 *
 * `legacy` është ajo që vendoste PARA komercializimit: një flamur i vjetër
 * admini (p.sh. `posterExportAllowed`), ose thjesht `true` për diçka që ka qenë
 * gjithmonë e lirë. Derisa pronari ta prekë dialin, ai mbetet i vetmi zë.
 *
 * Kur regjistri e merr kontrollin, veçoria shfaqet edhe kur s'zotërohet — me
 * bravë dhe me çmim pranë. Kjo është zgjedhje e ndërgjegjshme: standardi modern
 * i blerjes online nuk e fsheh atë që s'e ke blerë, e tregon dhe të thotë sa
 * kushton. E vetmja gjendje që fsheh vërtet është `hidden`.
 */
export function featureShown(key, legacy) {
  if (_managed[key] !== true) return legacy !== false;
  return _on[key] === true || _offered[key] === true;
}

/**
 * A LEJOHET vërtet të përdoret — motra e `featureShown`, por pa mëshirën e
 * vitrinës. Çifti është i qëllimshëm: `featureShown` vendos nëse butoni ekziston,
 * `featureAllowed` nëse klikimi bën punë. Një bravë e dukshme është reklamë; një
 * bravë që hapet vetë është dëm.
 *
 * Kjo është roja e fundit te klienti — jo e vetmja. Vendimi që numëron jetohet
 * te serveri; kjo vetëm kursen një udhëtim dhe një zhgënjim.
 */
export function featureAllowed(key, legacy) {
  if (_managed[key] !== true) return legacy !== false;
  return _on[key] === true;
}

export function onFeaturesChange(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/**
 * Serveri kthen `{ on, offered, managed }`. Para FAZA0e mungonte `managed`;
 * para FAZA0d kthehej një hartë e vetme, e sheshtë. I pranojmë të treja format.
 *
 * Kjo tolerancë nuk është mirësjellje — është domosdoshmëri: deploy-i i web-it
 * dhe ai i bazës nuk ndodhin në të njëjtin sekond. Çdo formë e vjetër zgjidhet
 * te `managed = {}`, pra "regjistri s'ka marrë asgjë në dorë" — dhe aplikacioni
 * sillet si më parë derisa të mbërrijë ana tjetër. Asnjë radhë deploy-i nuk
 * prodhon dot as bravë fantazmë, as buton të zhdukur.
 */
function normalize(data) {
  const d = data || {};
  if (d && typeof d === "object" && (d.on || d.offered || d.managed)) {
    return { on: d.on || {}, offered: d.offered || {}, managed: d.managed || {} };
  }
  return { on: d, offered: {}, managed: {} };
}

export async function loadFeatures(familyId) {
  if (!familyId) { _on = {}; _offered = {}; _managed = {}; _familyId = null; notify(); return {}; }
  _familyId = familyId;
  const { data, error } = await supabase.rpc("rpc_effective_features", { p_family_id: familyId });
  if (error) {
    console.warn("[veçoritë] rpc_effective_features dështoi:", error.message || error);
    return _on;   // mbaj të fundit të njohurat — mos i fik nga një gabim rrjeti
  }
  const next = normalize(data);
  // Mos njofto kot: krahasimi i thjeshtë ndal rivizatime pa ndryshim.
  if (JSON.stringify(next.on) !== JSON.stringify(_on) ||
      JSON.stringify(next.offered) !== JSON.stringify(_offered) ||
      JSON.stringify(next.managed) !== JSON.stringify(_managed)) {
    _on = next.on;
    _offered = next.offered;
    _managed = next.managed;
    notify();
  }
  return _on;
}

export function refreshFeatures() {
  if (_familyId) loadFeatures(_familyId);
}

function notify() {
  _listeners.forEach((fn) => { try { fn(_on); } catch (_e) { /* dëgjues i prishur — jo faji ynë */ } });
}
